/**
 * DataArchitecturePage — Vision architecture des données GS-Apps
 * Référence : ADR-094, ADR-020, ADR-021 — docs/architecture/
 * Page statique — zéro appel API.
 */
import { useState, type ReactNode, type ElementType } from 'react';
import type { TFunction } from 'i18next';
import { PageHeader } from '@gs/ui';
import {
  Database, ArrowRight, Layers, Server, Zap,
  CheckCircle, Clock, AlertCircle, Users,
} from 'lucide-react';
import { useTranslation } from '../i18n';

type Onglet = 'maintenant' | 'trajectoire' | 'medaillon' | 'sources';

function Badge({ color, children }: { color: string; children: ReactNode }) {
  const colors: Record<string, string> = {
    green:  'bg-emerald-100 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-300',
    blue:   'bg-blue-100 text-blue-700 dark:bg-blue-950/40 dark:text-blue-300',
    orange: 'bg-amber-100 text-amber-700 dark:bg-amber-950/40 dark:text-amber-300',
    slate:  'bg-slate-100 text-slate-600 dark:bg-slate-700 dark:text-slate-300',
    red:    'bg-red-100 text-red-700 dark:bg-red-950/40 dark:text-red-300',
  };
  return (
    <span className={`inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-meta font-semibold ${colors[color] ?? colors.slate}`}>
      {children}
    </span>
  );
}

function LayerCard({
  prefix, label, color, tables, desc,
}: {
  prefix: string; label: string; color: string;
  tables: string[]; desc: string;
}) {
  const bg: Record<string, string> = {
    bronze: 'border-amber-200 dark:border-amber-800 bg-amber-50/50 dark:bg-amber-950/20',
    silver: 'border-blue-200 dark:border-blue-800 bg-blue-50/50 dark:bg-blue-950/20',
    gold:   'border-yellow-200 dark:border-yellow-800 bg-yellow-50/50 dark:bg-yellow-950/20',
    app:    'border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800',
    shared: 'border-emerald-200 dark:border-emerald-800 bg-emerald-50/50 dark:bg-emerald-950/20',
  };
  return (
    <div className={`rounded-xl border p-4 ${bg[color] ?? bg.app}`}>
      <div className="flex items-center gap-2 mb-3">
        <span className="text-meta font-mono font-bold text-slate-500 dark:text-slate-400">{prefix}</span>
        <span className="text-field font-semibold text-slate-800 dark:text-slate-100">{label}</span>
      </div>
      <p className="text-meta text-slate-500 dark:text-slate-400 mb-3">{desc}</p>
      <div className="flex flex-wrap gap-1.5">
        {tables.map(t => (
          <code key={t} className="text-chip bg-white/70 dark:bg-slate-900/50 border border-slate-200 dark:border-slate-700 rounded-sm px-1.5 py-0.5 text-slate-600 dark:text-slate-300">
            {t}
          </code>
        ))}
      </div>
    </div>
  );
}

function TriggerCard({ icon: Icon, value, label, color }: {
  icon: ElementType; value: string; label: string; color: string;
}) {
  const colors: Record<string, string> = {
    red:    'text-red-500 bg-red-50 dark:bg-red-950/30',
    amber:  'text-amber-500 bg-amber-50 dark:bg-amber-950/30',
    blue:   'text-blue-500 bg-blue-50 dark:bg-blue-950/30',
  };
  return (
    <div className="flex items-center gap-3 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 p-4">
      <div className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-lg ${colors[color]}`}>
        <Icon size={20} />
      </div>
      <div>
        <p className="text-body font-bold text-slate-800 dark:text-slate-100">{value}</p>
        <p className="text-meta text-slate-500 dark:text-slate-400">{label}</p>
      </div>
    </div>
  );
}

function sofiBronzeTables(t: TFunction): string[] {
  return [
    'stg_sofi.Projets', 'stg_sofi.Vehicule', 'stg_sofi.PunchParJour', 'stg_sofi.LogImprRDS', 'stg_sofi.Phases',
    t('admin:dataArch.sources.moreTables'),
  ];
}
const STG_EXT = [
  'STG_JPL_Transactions', 'STG_PROFUEL_Transactions', 'STG_GEOTAB_Voyages', 'STG_SAAQ_Vehicule', 'STG_LOCATIONS_CourtTerme',
];
const DIM_TABLES = [
  'dim.branches', 'dim.employees', 'dim.vehicles', 'dim.customers', 'dim.projects', 'dim.DIM_Calendrier', 'dim.regions',
];
const MEDALLION_TABLES = [
  'silver.maintenance_orders', 'silver.security_rounds', 'gold.maintenance_cost_per_vehicle_month',
  'gold.security_compliance_rate', 'fact.plans', 'fact.invoices',
];
const APP_TABLES = ['adm.SyncLogs', 'adm.Apps', 'ptl.Stickers', 'dim.service_points (FK)', 'adm.EtlControl'];

function OngletMaintenant({ t }: { t: TFunction }) {
  const governance = [
    { rule: 'G22', desc: t('admin:dataArch.governance.g22') },
    { rule: 'G23', desc: t('admin:dataArch.governance.g23') },
    { rule: 'ADR-020', desc: t('admin:dataArch.governance.adr020') },
    { rule: 'ADR-017', desc: t('admin:dataArch.governance.adr017') },
  ];

  return (
    <div className="space-y-8">
      <div className="space-y-4">
        <h3 className="text-body font-semibold text-slate-700 dark:text-slate-200 flex items-center gap-2">
          <CheckCircle size={16} className="text-emerald-500" />
          {t('admin:dataArch.today.title')}
        </h3>

        <p className="text-meta text-slate-500 dark:text-slate-400">{t('admin:dataArch.layers.measureNote')}</p>

        <div className="grid gap-3 lg:grid-cols-2">
          <LayerCard
            prefix={t('admin:dataArch.layers.bronzeSofi.prefix')}
            label={t('admin:dataArch.layers.bronzeSofi.label')}
            color="bronze"
            desc={t('admin:dataArch.layers.bronzeSofi.desc')}
            tables={sofiBronzeTables(t)}
          />
          <LayerCard
            prefix={t('admin:dataArch.layers.bronzeExt.prefix')}
            label={t('admin:dataArch.layers.bronzeExt.label')}
            color="bronze"
            desc={t('admin:dataArch.layers.bronzeExt.desc')}
            tables={STG_EXT}
          />
          <LayerCard
            prefix={t('admin:dataArch.layers.dim.prefix')}
            label={t('admin:dataArch.layers.dim.label')}
            color="shared"
            desc={t('admin:dataArch.layers.dim.desc')}
            tables={DIM_TABLES}
          />
          <LayerCard
            prefix={t('admin:dataArch.layers.medallion.prefix')}
            label={t('admin:dataArch.layers.medallion.label')}
            color="gold"
            desc={t('admin:dataArch.layers.medallion.desc')}
            tables={MEDALLION_TABLES}
          />
          <LayerCard
            prefix={t('admin:dataArch.layers.app.prefix')}
            label={t('admin:dataArch.layers.app.label')}
            color="app"
            desc={t('admin:dataArch.layers.app.desc')}
            tables={APP_TABLES}
          />
        </div>

        <div className="rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 p-5">
          <p className="text-meta font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400 mb-4">
            {t('admin:dataArch.flux.title')}
          </p>
          <div className="flex flex-wrap items-center gap-2 text-field text-slate-600 dark:text-slate-300">
            <span className="font-semibold text-slate-800 dark:text-slate-100">{t('admin:dataArch.flux.sources')}</span>
            <ArrowRight size={14} className="text-slate-400" />
            <span className="font-mono text-amber-600 dark:text-amber-400">{t('admin:dataArch.flux.bronze')}</span>
            <ArrowRight size={14} className="text-slate-400" />
            <span className="font-mono text-emerald-600 dark:text-emerald-400">{t('admin:dataArch.flux.dim')}</span>
            <ArrowRight size={14} className="text-slate-400" />
            <span className="font-mono text-blue-600 dark:text-blue-400">{t('admin:dataArch.flux.silver')}</span>
            <ArrowRight size={14} className="text-slate-400" />
            <span className="font-mono text-yellow-600 dark:text-yellow-400">{t('admin:dataArch.flux.gold')}</span>
          </div>
          <div className="mt-3 flex flex-wrap items-center gap-2 text-field text-slate-600 dark:text-slate-300">
            <span className="font-semibold text-slate-800 dark:text-slate-100">{t('admin:dataArch.flux.appsStart')}</span>
            <ArrowRight size={14} className="text-slate-400" />
            <span className="font-mono text-slate-600 dark:text-slate-300">{t('admin:dataArch.flux.appsLine')}</span>
            <ArrowRight size={14} className="text-slate-400" />
            <span className="italic text-slate-400">{t('admin:dataArch.flux.appsTail')}</span>
          </div>
        </div>

        <div className="rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 p-5">
          <p className="text-meta font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400 mb-3">
            {t('admin:dataArch.governance.title')}
          </p>
          <div className="space-y-2 text-field text-slate-600 dark:text-slate-300">
            {governance.map(({ rule, desc }) => (
              <div key={rule} className="flex gap-3">
                <Badge color="blue">{rule}</Badge>
                <span>{desc}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

function OngletTrajectoire({ t }: { t: TFunction }) {
  const coutures = [t('admin:dataArch.trajectory.couture1'), t('admin:dataArch.trajectory.couture2')];
  const questions = [
    t('admin:dataArch.trajectory.questionA'),
    t('admin:dataArch.trajectory.questionB'),
    t('admin:dataArch.trajectory.questionC'),
  ];

  return (
    <div className="space-y-6">
      <div className="rounded-xl border border-amber-200 dark:border-amber-800 bg-amber-50/50 dark:bg-amber-950/20 p-5">
        <div className="flex items-center gap-2 mb-2">
          <Clock size={16} className="text-amber-600" />
          <span className="text-field font-semibold text-amber-800 dark:text-amber-200">
            {t('admin:dataArch.trajectory.title')}
          </span>
        </div>
        <p className="text-meta text-amber-700 dark:text-amber-300">{t('admin:dataArch.trajectory.intro')}</p>
      </div>

      <div className="rounded-xl border border-blue-200 dark:border-blue-800 bg-blue-50/50 dark:bg-blue-950/20 p-5">
        <div className="flex items-center gap-2 mb-3">
          <Server size={16} className="text-blue-600" />
          <span className="text-field font-semibold text-blue-800 dark:text-blue-200">
            {t('admin:dataArch.trajectory.baseTitle')}
          </span>
        </div>
        <Badge color="blue">{t('admin:dataArch.trajectory.baseBadge')}</Badge>
      </div>

      <div>
        <h3 className="text-field font-semibold text-slate-700 dark:text-slate-200 mb-3">
          {t('admin:dataArch.trajectory.chargeTitle')}
        </h3>
        <p className="text-field text-slate-600 dark:text-slate-300">{t('admin:dataArch.trajectory.chargeIntro')}</p>
      </div>

      <div>
        <h3 className="text-field font-semibold text-slate-700 dark:text-slate-200 mb-3">
          {t('admin:dataArch.trajectory.triggerTitle')}
        </h3>
        <TriggerCard icon={Zap} color="amber" value={t('admin:dataArch.trajectory.triggerValue')} label={t('admin:dataArch.trajectory.triggerLabel')} />
      </div>

      <div className="rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 p-5">
        <p className="text-meta font-semibold uppercase tracking-wider text-slate-500 mb-3">
          {t('admin:dataArch.trajectory.questionTitle')}
        </p>
        <p className="text-field text-slate-600 dark:text-slate-300 mb-3">{t('admin:dataArch.trajectory.questionIntro')}</p>
        <div className="space-y-2 text-field text-slate-600 dark:text-slate-300">
          {questions.map((item) => (
            <div key={item} className="flex gap-2">
              <AlertCircle size={14} className="text-amber-500 shrink-0 mt-0.5" />
              <span>{item}</span>
            </div>
          ))}
        </div>
      </div>

      <div className="rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 p-5">
        <p className="text-meta font-semibold uppercase tracking-wider text-slate-500 mb-3">
          {t('admin:dataArch.trajectory.couturesTitle')}
        </p>
        <div className="space-y-2 text-field text-slate-600 dark:text-slate-300">
          {coutures.map((item) => (
            <div key={item} className="flex gap-2">
              <CheckCircle size={14} className="text-emerald-500 shrink-0 mt-0.5" />
              <span>{item}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function OngletMedaillon({ t }: { t: TFunction }) {
  const planned: { app: string; prefix: string; desc: string; statusLabel: string }[] = [
    { app: 'GS-Flotte', prefix: 'flt.*', desc: t('admin:dataArch.planned.flotte'), statusLabel: t('admin:dataArch.planned.status.plan') },
    { app: 'GS-Ops', prefix: 'ops.*', desc: t('admin:dataArch.planned.ops'), statusLabel: t('admin:dataArch.planned.status.plan') },
    { app: 'GS-ShopHub', prefix: 'mec.*', desc: t('admin:dataArch.planned.shophub'), statusLabel: t('admin:dataArch.planned.status.backlog') },
    { app: 'Extracteur Geotab', prefix: 'stg_geotab.*', desc: t('admin:dataArch.planned.geotab'), statusLabel: t('admin:dataArch.planned.status.backlog') },
    { app: 'Extracteur JPL', prefix: 'stg_jpl.*', desc: t('admin:dataArch.planned.jpl'), statusLabel: t('admin:dataArch.planned.status.backlog') },
  ];

  return (
    <div className="space-y-6">
      <div className="rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 p-5">
        <div className="flex items-center gap-2 mb-3">
          <Layers size={16} className="text-violet-500" />
          <span className="text-field font-semibold text-slate-800 dark:text-slate-100">
            {t('admin:dataArch.medallion.title')}
          </span>
        </div>
        <p className="text-meta text-slate-500 dark:text-slate-400 mb-4">{t('admin:dataArch.medallion.intro')}</p>
        <p className="text-field text-slate-600 dark:text-slate-300 font-mono text-meta">{t('admin:dataArch.medallion.readRules')}</p>
      </div>

      <div className="rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 p-5">
        <p className="text-meta font-semibold uppercase tracking-wider text-slate-500 mb-3">
          {t('admin:dataArch.planned.title')}
        </p>
        <div className="space-y-3">
          {planned.map(({ app, prefix, desc, statusLabel }, i) => (
            <div key={app} className="flex items-start gap-3">
              <Badge color={i < 2 ? 'blue' : 'slate'}>{statusLabel}</Badge>
              <div>
                <span className="text-field font-semibold text-slate-800 dark:text-slate-100">{app}</span>
                <span className="text-meta font-mono text-slate-400 ml-2">{prefix}</span>
                <p className="text-meta text-slate-500 dark:text-slate-400">{desc}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function OngletSources({ t }: { t: TFunction }) {
  const layers = [
    { couche: t('admin:dataArch.sources.layer1'), source: t('admin:dataArch.sources.layer1Source'), role: t('admin:dataArch.sources.layer1Role'), color: 'text-blue-600 dark:text-blue-400' },
    { couche: t('admin:dataArch.sources.layer2'), source: t('admin:dataArch.sources.layer2Source'), role: t('admin:dataArch.sources.layer2Role'), color: 'text-emerald-600 dark:text-emerald-400' },
    { couche: t('admin:dataArch.sources.layer3'), source: t('admin:dataArch.sources.layer3Source'), role: t('admin:dataArch.sources.layer3Role'), color: 'text-violet-600 dark:text-violet-400' },
  ];

  const populations = [
    { table: 'Employes', actifs: '478', total: '3 830', desc: t('admin:dataArch.sources.pop.employes'), color: 'text-brand-500 bg-brand-50 dark:bg-brand-950/30' },
    { table: 'Usagers', actifs: '106', total: '303', desc: t('admin:dataArch.sources.pop.usagers'), color: 'text-blue-500 bg-blue-50 dark:bg-blue-950/30' },
    { table: 'UsagersCombine', actifs: '108', total: '108', desc: t('admin:dataArch.sources.pop.usagersCombine'), color: 'text-violet-500 bg-violet-50 dark:bg-violet-950/30' },
    { table: 'PunchParJour', actifs: '850 675', total: 'rows', desc: t('admin:dataArch.sources.pop.punch'), color: 'text-slate-500 bg-slate-50 dark:bg-slate-800' },
  ];

  const branches = [
    { code: 'SE',  nom: 'Signalisation Estrie',       actifs: 70  },
    { code: 'SD',  nom: 'Signalisation Drummond',      actifs: 63  },
    { code: 'SG',  nom: 'Signalisation Gatineau',      actifs: 53  },
    { code: 'SSM', nom: 'Signalisation MTL',           actifs: 52  },
    { code: 'SM',  nom: 'Signalisation Montérégie',    actifs: 52  },
    { code: 'SLI', nom: 'Signalisation Laurentienne',  actifs: 44  },
    { code: 'SQB', nom: 'Signalisation Québec-Beauce', actifs: 32  },
    { code: 'SLA', nom: 'Signalisation Lanaudière',    actifs: 31  },
    { code: 'SMA', nom: 'Signalisation Mauricie',      actifs: 28  },
    { code: 'SAI', nom: 'Signalisation Abitibi',       actifs: 17  },
    { code: 'SSA', nom: 'Signalisation Saguenay',      actifs: 12  },
    { code: 'AXC', nom: 'Axe Conseil',                 actifs: 9,  tenant: true },
    { code: 'SAT', nom: 'Succursale Satellite',        actifs: 8   },
    { code: 'SÉB', nom: 'Sécuriblocs',                 actifs: 6,  tenant: true },
    { code: 'FLO', nom: 'Signalisation Flotte',        actifs: 1   },
  ];

  const roles = [
    { role: t('admin:dataArch.sources.roleRows.cp'), col: 'Charge_Projet', nb: 27 },
    { role: t('admin:dataArch.sources.roleRows.est'), col: 'Estimateur', nb: 16 },
    { role: t('admin:dataArch.sources.roleRows.des'), col: 'Dessinateur', nb: 10 },
    { role: t('admin:dataArch.sources.roleRows.ing'), col: 'Ingenieur_Signalisation', nb: 6 },
    { role: t('admin:dataArch.sources.roleRows.dir'), col: 'Directeur_Succursale', nb: 3 },
  ];

  const emails = [
    { label: '@groupesignalisation.ca', value: '5', note: t('admin:dataArch.sources.email.gs'), color: 'text-emerald-600 dark:text-emerald-400' },
    { label: t('admin:dataArch.sources.email.personal'), value: '414', note: t('admin:dataArch.sources.email.personalNote'), color: 'text-amber-600 dark:text-amber-400' },
    { label: t('admin:dataArch.sources.email.none'), value: '59', note: '', color: 'text-slate-400' },
  ];

  return (
    <div className="space-y-8">
      <div className="space-y-3">
        <h3 className="text-body font-semibold text-slate-700 dark:text-slate-200 flex items-center gap-2">
          <CheckCircle size={16} className="text-emerald-500" />
          {t('admin:dataArch.sources.adr021Title')}
        </h3>
        <div className="rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 overflow-hidden">
          <table className="w-full text-field">
            <thead>
              <tr className="border-b border-slate-100 dark:border-slate-700">
                <th className="text-left px-4 py-2.5 text-meta font-semibold uppercase tracking-wider text-slate-400">{t('admin:dataArch.sources.tableCouche')}</th>
                <th className="text-left px-4 py-2.5 text-meta font-semibold uppercase tracking-wider text-slate-400">{t('admin:dataArch.sources.tableSource')}</th>
                <th className="text-left px-4 py-2.5 text-meta font-semibold uppercase tracking-wider text-slate-400">{t('admin:dataArch.sources.tableRole')}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 dark:divide-slate-700">
              {layers.map(({ couche, source, role, color }) => (
                <tr key={couche}>
                  <td className={`px-4 py-3 font-semibold ${color}`}>{couche}</td>
                  <td className="px-4 py-3"><code className="text-meta bg-slate-100 dark:bg-slate-900 px-1.5 py-0.5 rounded-sm">{source}</code></td>
                  <td className="px-4 py-3 text-slate-500 dark:text-slate-400">{role}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="space-y-3">
        <h3 className="text-body font-semibold text-slate-700 dark:text-slate-200">
          {t('admin:dataArch.sources.auditDate')}
        </h3>
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          {populations.map(({ table, actifs, total, desc, color }) => (
            <div key={table} className="rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 p-4 flex items-center gap-3">
              <div className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-lg ${color}`}>
                <Database size={18} />
              </div>
              <div>
                <p className="text-head font-bold text-slate-800 dark:text-slate-100 leading-none">{actifs}</p>
                <p className="text-chip text-slate-400 mt-0.5">{t('admin:dataArch.sources.pop.actifsLabel', { total })}</p>
                <p className="text-meta font-medium text-slate-500 dark:text-slate-400 mt-1">{table}</p>
                <p className="text-meta text-slate-400">{desc}</p>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        <div className="space-y-3">
          <h3 className="text-body font-semibold text-slate-700 dark:text-slate-200">
            {t('admin:dataArch.sources.branchesTitle')}
          </h3>
          <div className="rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 overflow-hidden">
            <table className="w-full text-meta">
              <thead>
                <tr className="border-b border-slate-100 dark:border-slate-700 bg-slate-50 dark:bg-slate-900/50">
                  <th className="text-left px-3 py-2 text-chip font-bold uppercase tracking-wider text-slate-400">{t('admin:dataArch.sources.colCode')}</th>
                  <th className="text-left px-3 py-2 text-chip font-bold uppercase tracking-wider text-slate-400">{t('admin:dataArch.sources.colBranch')}</th>
                  <th className="text-right px-3 py-2 text-chip font-bold uppercase tracking-wider text-slate-400">{t('admin:dataArch.sources.colActive')}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 dark:divide-slate-700">
                {branches.map((row) => (
                  <tr key={row.code}>
                    <td className="px-3 py-2 font-mono font-bold text-slate-600 dark:text-slate-300">{row.code}</td>
                    <td className="px-3 py-2 text-slate-700 dark:text-slate-300">
                      {row.nom}
                      {'tenant' in row && row.tenant && (
                        <span className="ml-1.5 inline-flex items-center text-chip font-semibold bg-blue-100 dark:bg-blue-950/40 text-blue-700 dark:text-blue-300 px-1.5 py-0.5 rounded-full">
                          {t('admin:dataArch.sources.sameAdTenant')}
                        </span>
                      )}
                    </td>
                    <td className="px-3 py-2 text-right font-semibold text-slate-800 dark:text-slate-100">{row.actifs}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <div className="space-y-6">
          <div className="space-y-3">
            <h3 className="text-body font-semibold text-slate-700 dark:text-slate-200">
              {t('admin:dataArch.sources.rolesTitle')}
            </h3>
            <div className="rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 overflow-hidden">
              <table className="w-full text-meta">
                <thead>
                  <tr className="border-b border-slate-100 dark:border-slate-700 bg-slate-50 dark:bg-slate-900/50">
                    <th className="text-left px-3 py-2 text-chip font-bold uppercase tracking-wider text-slate-400">{t('admin:dataArch.sources.colRole')}</th>
                    <th className="text-left px-3 py-2 text-chip font-bold uppercase tracking-wider text-slate-400">{t('admin:dataArch.sources.colSofi')}</th>
                    <th className="text-right px-3 py-2 text-chip font-bold uppercase tracking-wider text-slate-400">{t('admin:dataArch.sources.colCount')}</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 dark:divide-slate-700">
                  {roles.map(({ role, col, nb }) => (
                    <tr key={col}>
                      <td className="px-3 py-2 text-slate-700 dark:text-slate-300">{role}</td>
                      <td className="px-3 py-2"><code className="text-chip bg-slate-100 dark:bg-slate-900 px-1 py-0.5 rounded-sm">{col}</code></td>
                      <td className="px-3 py-2 text-right font-semibold text-slate-800 dark:text-slate-100">{nb}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <div className="space-y-3">
            <h3 className="text-body font-semibold text-slate-700 dark:text-slate-200">
              {t('admin:dataArch.sources.emailTitle')}
            </h3>
            <div className="rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 p-4 space-y-3">
              {emails.map(({ label, value, note, color }) => (
                <div key={label} className="flex items-center justify-between">
                  <div>
                    <p className="text-field text-slate-700 dark:text-slate-300">{label}</p>
                    {note && <p className="text-meta text-slate-400">{note}</p>}
                  </div>
                  <span className={`text-head font-bold ${color}`}>{value}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function DataArchitecturePage() {
  const { t } = useTranslation();
  const [onglet, setOnglet] = useState<Onglet>('maintenant');

  const onglets: { key: Onglet; label: string; icon: ElementType }[] = [
    { key: 'maintenant', label: t('admin:dataArch.tabs.today'), icon: CheckCircle },
    { key: 'trajectoire', label: t('admin:dataArch.tabs.trajectory'), icon: Clock },
    { key: 'medaillon', label: t('admin:dataArch.tabs.medallion'), icon: Layers },
    { key: 'sources', label: t('admin:dataArch.tabs.sources'), icon: Users },
  ];

  return (
    <div className="space-y-6 p-6">
      <PageHeader
        title={t('admin:dataArch.page.title')}
        description={t('admin:dataArch.page.description')}
      />

      <div className="flex flex-wrap gap-1 bg-slate-100 dark:bg-slate-800 rounded-xl p-1 w-fit max-w-full touch-manipulation">
        {onglets.map(({ key, label, icon: Icon }) => (
          <button
            key={key}
            type="button"
            onClick={() => setOnglet(key)}
            className={`flex items-center gap-2 px-4 py-1.5 rounded-lg text-field font-medium transition-colors touch-manipulation ${
              onglet === key
                ? 'bg-white dark:bg-slate-700 text-slate-800 dark:text-slate-100 shadow-xs'
                : 'text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-300'
            }`}
          >
            <Icon size={14} />
            {label}
          </button>
        ))}
      </div>

      {onglet === 'maintenant' && <OngletMaintenant t={t} />}
      {onglet === 'trajectoire' && <OngletTrajectoire t={t} />}
      {onglet === 'medaillon' && <OngletMedaillon t={t} />}
      {onglet === 'sources' && <OngletSources t={t} />}
    </div>
  );
}
